/**
 * "Set" bundle add-to-cart.
 *
 * Some products sell a phantom "Set" size variant per color (e.g. Set /
 * Oat) that exists only so it can be selected in the native variant
 * picker and show its own price. It never actually gets added to the
 * cart: selecting it and submitting the product form instead adds the
 * real sibling variants for every other size value of that same color
 * (e.g. Original + Large - whatever the other size values are named).
 *
 * `snippets/product-variant-picker.liquid` renders a JSON map (outside
 * `variant-selects`, as `[data-set-bundle-map]`) from each phantom Set
 * variant id to its sibling variant ids. This script reads that map at
 * submit time and, when the currently selected variant is one of those
 * phantom ids, swaps the single add-to-cart request for a multi-item
 * one.
 *
 * Listeners capture on `document` so the swap beats the theme's own handling
 * without editing `theme.js` or `cart-bar.js`, which are minified. Dynamic
 * checkout can't be intercepted at all, so `snippets/product-buttons.liquid`
 * hides those buttons for Set variants instead.
 */
(() => {
  // Guard against double-binding: this script tag is rendered once per
  // product-info, but if that markup is ever re-executed (e.g. a future
  // swap-product flow re-injects it), a second listener would add every
  // bundle line item twice per submit.
  if (window.themeProductSetBundleBound) return;
  window.themeProductSetBundleBound = true;

  /**
   * Read the sibling-variant bundle for the currently selected variant.
   *
   * @param {HTMLElement} productInfo - the <product-info> wrapping the PDP.
   * @returns {{label: string, variantIds: number[]}|null} the bundle to add,
   *   or null when the selected variant is not a phantom Set variant.
   */
  function getBundlePair(productInfo) {
    const mapScript = productInfo.querySelector('[data-set-bundle-map]');
    const selectedVariantScript = productInfo.querySelector('variant-selects [data-selected-variant]');

    if (!mapScript || !selectedVariantScript) return null;

    let bundleMap;
    let selectedVariant;

    try {
      bundleMap = JSON.parse(mapScript.textContent);
      selectedVariant = JSON.parse(selectedVariantScript.textContent);
    } catch (error) {
      return null;
    }

    if (!selectedVariant || !bundleMap[selectedVariant.id]) return null;

    return bundleMap[selectedVariant.id];
  }

  /**
   * Add the selected variant's sibling bundle to the cart, in place of the
   * phantom Set variant the event would otherwise have added.
   *
   * @param {Event} event - the submit or click being intercepted.
   * @param {HTMLElement} productInfo - the <product-info> wrapping the PDP.
   * @param {HTMLElement|null} loadingButton - button to show the loading state on.
   * @returns {boolean} true when the bundle was added and the event consumed.
   */
  function addBundleToCart(event, productInfo, loadingButton) {
    const bundlePair = getBundlePair(productInfo);
    if (!bundlePair) return false;

    const cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.addToCart !== 'function') return false;

    event.preventDefault();
    event.stopPropagation();

    const quantityInput = productInfo.querySelector('[data-product-form] [name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
    const setProperty = `${productInfo.dataset.productHandle}-${bundlePair.label}`;

    const items = bundlePair.variantIds.map((variantId) => ({
      id: variantId,
      quantity,
      properties: { _set: setProperty },
    }));

    cartItems.addToCart(items, loadingButton);
    return true;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;

      const productInfo = form.closest('product-info');
      if (!productInfo) return;

      addBundleToCart(event, productInfo, form.querySelector('[type="submit"]'));
    },
    true
  );

  // cart-bar.js calls `product-form.onSubmitHandler` directly, so no submit
  // event ever fires for the sticky bar - catch its click during capture.
  document.addEventListener(
    'click',
    (event) => {
      const cartBarButton = event.target.closest?.('[data-cart-bar-add-to-cart]');
      if (!cartBarButton) return;

      const productInfo = cartBarButton.closest('product-info');
      if (!productInfo) return;

      addBundleToCart(event, productInfo, cartBarButton);
    },
    true
  );
})();
